import { useEffect, useMemo, useState } from "react";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { REF, breadcrumb, graph, videoObject } from "../lib/schema";
import GoogleReviewsLive from "../components/site/GoogleReviewsLive";
import { CLINIC } from "../lib/clinic";
import YouTubeEmbed from "../components/site/YouTubeEmbed";
import ReviewCard from "../components/site/ReviewCard";

const Testimonials = () => {
  usePageMeta({
    title: "Patient Testimonials & Reviews | Dr. Shantanu Kumar Gupta, Varanasi",
    path: "/testimonials",
    description: "Real patient stories, video testimonials and Google-style reviews for cataract, retina and second-opinion care with Dr. Shantanu Kumar Gupta.",
  });
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const videos = items.filter((t) => t.video_id);
  const textOnly = items.filter((t) => !t.video_id);
  const allRatings = [...items.map((t) => t.rating || 5), ...reviews.map((r) => r.rating || 5)];
  const avg = allRatings.length
    ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(1)
    : "5.0";

  const schema = useMemo(() => {
    const nodes = [
      breadcrumb([{ name: "Home", path: "/" }, { name: "Testimonials", path: "/testimonials" }]),
    ];
    // VideoObject nodes for every testimonial with a YouTube video — makes
    // the page eligible for video rich results.
    videos.slice(0, 20).forEach((t) => {
      const v = videoObject({
        id: `testimonial-${t.video_id || t.id || t.name}`.toLowerCase().replace(/\s+/g, "-"),
        pagePath: "/testimonials",
        name: t.video_title || `${t.name} — ${t.procedure} testimonial`,
        description: t.quote || `Patient testimonial for ${t.procedure} at Shantanu Netralaya, Varanasi.`,
        videoRef: t.video_id,
        uploadDate: t.date || t.updated_at,
      });
      if (v) nodes.push(v);
    });
    if (allRatings.length) {
      nodes.push({
        "@type": "MedicalClinic",
        "@id": REF.clinic,
        name: CLINIC.name,
        url: window.location.origin + "/",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: avg,
          ratingCount: allRatings.length,
          bestRating: 5,
          worstRating: 1,
        },
        review: [
          ...items.map((t) => ({
            "@type": "Review",
            name: `Patient review by ${t.name}`,
            author: { "@type": "Person", name: t.name },
            reviewRating: { "@type": "Rating", ratingValue: t.rating || 5, bestRating: 5, worstRating: 1 },
            reviewBody: t.quote,
            itemReviewed: {
              "@type": "MedicalClinic",
              "@id": REF.clinic,
              name: CLINIC.name,
            },
          })),
          ...reviews.map((r) => ({
            "@type": "Review",
            name: `Google review by ${r.author_name}`,
            author: { "@type": "Person", name: r.author_name },
            reviewRating: { "@type": "Rating", ratingValue: r.rating || 5, bestRating: 5, worstRating: 1 },
            reviewBody: r.text,
            itemReviewed: {
              "@type": "MedicalClinic",
              "@id": REF.clinic,
              name: CLINIC.name,
            },
          })),
        ],
      });
    }
    return graph(nodes);
  }, [items, reviews, avg, allRatings.length, videos]);
  usePageJsonLd(schema);

  return (
    <div data-testid="testimonials-page">
      <section className="pt-20 pb-6 container-page">
        <div className="overline mb-4">Patient Voices</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Real stories of restored vision.
        </h1>
        <div className="mt-6 flex items-center gap-3 text-sm">
          <div className="flex gap-0.5 text-[hsl(var(--accent))]">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
            ))}
          </div>
          <span className="font-serif text-lg">{avg}</span>
          <span className="text-muted-foreground text-xs uppercase tracking-[0.22em]">
            · {allRatings.length} verified voices
          </span>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={CLINIC.googleProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full border border-border bg-white hover:border-[hsl(var(--primary))]/60 hover:shadow-sm transition group"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-xs uppercase tracking-[0.16em] font-medium">
              <span className="text-[hsl(var(--foreground))]">{CLINIC.googleRating}★</span>
              <span className="text-[hsl(var(--muted-foreground))] mx-1.5">·</span>
              Read all reviews on Google
            </span>
            <ArrowUpRight size={13} strokeWidth={2} className="opacity-60 group-hover:opacity-100" />
          </a>
          <a
            href={CLINIC.googleWriteReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-write-review-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[hsl(var(--accent))]/50 text-xs uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/8 transition"
          >
            <Star size={13} strokeWidth={1.75} /> Write a Google review
          </a>
        </div>
      </section>

      {/* Live Google reviews — pulled fresh every 6h from Places API (New) */}
      <GoogleReviewsLive variant="grid" limit={6} />

      {/* Video testimonials */}
      {videos.length > 0 && (
        <section className="py-10 container-page" data-testid="video-testimonials">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Watch</div>
          <h2 className="font-serif text-3xl">Video testimonials</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((t) => (
              <div key={t.name} data-testid={`video-testimonial-${t.name.replace(/\s/g, "-").toLowerCase()}`}>
                <YouTubeEmbed src={t.video_id} title={t.video_title || `${t.name} — ${t.procedure}`} />
                <div className="mt-4">
                  <div className="font-serif text-lg">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.procedure} · {t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Google-style reviews */}
      {reviews.length > 0 && (
        <section className="py-10 container-page" data-testid="reviews-section">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Reviews</div>
          <h2 className="font-serif text-3xl">What patients say online</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r) => <ReviewCard key={r.author_name} review={r} />)}
          </div>
        </section>
      )}

      {/* Long-form testimonials */}
      <section className="py-10 container-page" data-testid="testimonials-grid-section">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Patient stories</div>
        <h2 className="font-serif text-3xl">In their own words</h2>
        <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {textOnly.map((t) => (
            <div key={t.name} className="card-elegant p-8" data-testid={`review-${t.name.replace(/\s/g, "-").toLowerCase()}`}>
              <Quote size={26} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
              <p className="mt-4 prose-elegant">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-6 pt-6 border-t border-border/60">
                <div className="font-serif text-lg">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.procedure} · {t.location}</div>
                <div className="mt-2 flex gap-0.5 text-[hsl(var(--accent))]">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
